import React, { useEffect, useState } from "react";
import { FiArrowRightCircle, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ActionsMenu from "../../components/common/actionsMenu/ActionsMenu";
import ConfirmDeleteModal from "../../components/common/deleteModal/ConfirmDeleteModal.js";
import PageHeader from "../../components/common/pageheader/PageHeader.js";
import Loader from "../../components/common/Loader/Loader.js";
import TableEmptyState from "../../components/common/tableEmptyState/TableEmptyState.js";

import { createSubject, deleteSubject, getSubjects, updateSubject } from "../../services/subjectsApi.js";

import SubjectFormModal from "./subjectBaseModal/SubjectFormModal.js";
import styles from "./SubjectBasePage.module.css";

const SubjectBasePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [searchDescription, setSearchDescription] = useState("");
  const [appliedSearch, setAppliedSearch] = useState({ name: "", description: "" });

  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");

  const [formValues, setFormValues] = useState({
    id: null,
    name: "",
    description: "",
  });

  const [saving, setSaving] = useState(false);

  const [expandedSubjectId, setExpandedSubjectId] = useState(null);
  const [topicsBySubject, setTopicsBySubject] = useState({});
  const [topicsLoadingId, setTopicsLoadingId] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [subjectToDelete, setSubjectToDelete] = useState(null);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      setError("");

      const filters = { pageNo, pageSize };

      if (search.trim()) filters.name = search.trim();
      if (searchDescription.trim()) filters.description = searchDescription.trim();

      setAppliedSearch({ name: search.trim() || "", description: searchDescription.trim() || "" });

      const data = await getSubjects(filters);
      const raw = data?.content || data || [];

      const baseSubjects = raw.filter((s) => s.parentId === null || s.parentId === 0 || s.parentId === undefined);

      setSubjects(baseSubjects);

      const total =
        typeof data?.totalPages === "number" && data.totalPages > 0
          ? data.totalPages
          : Math.ceil(raw.length / pageSize) || 1;

      setTotalPages(total);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.subjectBase.loadError");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, [pageNo]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPageNo(1);
    fetchSubjects();
  };

  const openCreateModal = () => {
    setFormMode("create");
    setFormValues({ id: null, name: "", description: "" });
    setIsModalOpen(true);
  };

  const openEditModal = (subject) => {
    setFormMode("edit");
    setFormValues({
      id: subject.id,
      name: subject.name || "",
      description: subject.description || "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormValues({ id: null, name: "", description: "" });
    setError("");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (!formValues.name.trim()) {
        setError(t("pages.subjectBase.nameRequired"));
        setSaving(false);
        return;
      }

      if (formMode === "create") {
        await createSubject({
          name: formValues.name.trim(),
          description: formValues.description?.trim() || "",
        });

        toast.success(t("pages.subjectBase.createSuccess"), { toastId: "subject-create-success" });
      } else {
        await updateSubject({
          id: formValues.id,
          name: formValues.name.trim(),
          description: formValues.description?.trim() || "",
        });

        toast.success(t("pages.subjectBase.updateSuccess"), { toastId: "subject-update-success" });
      }

      closeModal();
      setPageNo(1);
      fetchSubjects();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.subjectBase.operationFailed");
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (subject) => {
    setSubjectToDelete(subject);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      setError("");

      await deleteSubject(subjectToDelete.id);

      toast.success(t("pages.subjectBase.deleteSuccess"));

      setDeleteModalOpen(false);
      setSubjectToDelete(null);
      setPageNo(1);
      fetchSubjects();
    } catch (err) {
      const msg = err?.response?.data?.message || t("pages.subjectBase.deleteFailed");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setSubjectToDelete(null);
  };

  const goToSubjectDetail = (subjectId) => {
    navigate(`/subjects/${subjectId}`);
  };

  const fetchTopicsForSubject = async (subjectId) => {
    try {
      setTopicsLoadingId(subjectId);
      setError("");

      const data = await getSubjects({
        parentId: subjectId,
        pageNo: 1,
        pageSize: 100,
      });

      const list = Array.isArray(data?.content) ? data.content : [];

      setTopicsBySubject((prev) => ({
        ...prev,
        [subjectId]: list,
      }));
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.subjectBase.topicsLoadError");
      setError(msg);
    } finally {
      setTopicsLoadingId(null);
    }
  };

  const toggleTopics = (subjectId) => {
    if (expandedSubjectId === subjectId) {
      setExpandedSubjectId(null);
      return;
    }

    setExpandedSubjectId(subjectId);

    if (!topicsBySubject[subjectId]) {
      fetchTopicsForSubject(subjectId);
    }
  };

  const goToPrevPage = () => {
    setPageNo((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    if (pageNo < totalPages) {
      setPageNo((prev) => prev + 1);
    }
  };

  return (
    <div className={styles.container}>
      <PageHeader title={t("pages.subjectBase.title")} />

      <form onSubmit={handleSearchSubmit} className={styles.filterForm}>
        <input
          type="text"
          placeholder={t("pages.subjectBase.searchByName")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.searchInput}
        />

        <input
          type="text"
          placeholder={t("pages.subjectBase.searchByDescription")}
          value={searchDescription}
          onChange={(e) => setSearchDescription(e.target.value)}
          className={styles.searchInput}
        />

        <button type="submit" className={styles.primaryBtn}>
          {t("common.search")}
        </button>

        <button type="button" onClick={openCreateModal} className={styles.secondaryBtn}>
          {t("pages.subjectBase.newSubject")}
        </button>
      </form>

      {loading && <Loader fullscreen />}
      {error && <p className={styles.error}>{t("common.errorLabel")}: {error}</p>}

      {!loading && subjects.length === 0 && !error && (
        <TableEmptyState
          title={t("pages.subjectBase.noSubjectsFound")}
          description={appliedSearch.name || appliedSearch.description ? t("pages.subjectBase.changeCriteria") : t("pages.subjectBase.addNew")}
          icon={appliedSearch.name || appliedSearch.description ? "search" : "inbox"}
        />
      )}

      {!loading && subjects.length > 0 && (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t("pages.subjectBase.subjectName")}</th>
                <th>{t("pages.subjectBase.description")}</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {subjects.map((s) => {
                const isExpanded = expandedSubjectId === s.id;
                const topics = topicsBySubject[s.id] || [];

                return (
                  <React.Fragment key={s.id}>
                    <tr>
                      <td>{s.name}</td>
                      <td>{s.description}</td>

                      <td className={styles.actionsCell}>
                        <div className={styles.actions}>
                          <button
                            type="button"
                            onClick={() => toggleTopics(s.id)}
                            className={`${styles.toggleIconBtn} ${isExpanded ? styles.toggleIconBtnActive : ""}`}
                            title={t("pages.subjectBase.topicsTitle")}
                          >
                            {isExpanded ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
                          </button>

                          <button type="button" onClick={() => goToSubjectDetail(s.id)} className={styles.iconBtn}>
                            <FiArrowRightCircle size={18} />
                          </button>

                          <ActionsMenu onEdit={() => openEditModal(s)} onDelete={() => handleDeleteClick(s)} />
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className={styles.expandedRow}>
                        <td colSpan={3} className={styles.expandedCell}>
                          <div className={styles.topicPanel}>
                            <div className={styles.topicPanelHeader}>
                              <span className={styles.topicPanelTitle}>{t("pages.subjectBase.topicsTitle")}</span>
                              {topicsLoadingId !== s.id && topics.length > 0 && (
                                <span className={styles.topicPanelCount}>{topics.length}</span>
                              )}
                            </div>

                            {topicsLoadingId === s.id ? (
                              <div className={styles.topicHint}>{t("pages.subjectBase.topicsLoading")}</div>
                            ) : topics.length === 0 ? (
                              <div className={styles.topicHint}>{t("pages.subjectBase.noTopicsForSubject")}</div>
                            ) : (
                              <div className={styles.topicGrid}>
                                {topics.map((tp) => (
                                  <button
                                    key={tp.id}
                                    type="button"
                                    className={styles.topicCard}
                                    onClick={() => navigate(`/question-bank?subjectId=${tp.id}`)}
                                    title={t("pages.subjectDetail.manageQuestions")}
                                  >
                                    <span className={styles.topicCardName}>{tp.name}</span>
                                    {tp.description && <span className={styles.topicCardDesc}>{tp.description}</span>}
                                    <FiArrowRightCircle className={styles.topicCardArrow} size={16} />
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          <div className={styles.pagination}>
            <button type="button" onClick={goToPrevPage} disabled={pageNo === 1}>
              {t("common.previous")}
            </button>

            <span>
              {t("common.pageOf", { page: pageNo, total: totalPages || 1 })}
            </span>

            <button type="button" onClick={goToNextPage} disabled={pageNo >= totalPages}>
              {t("common.next2")}
            </button>
          </div>
        </>
      )}

      <SubjectFormModal
        isOpen={isModalOpen}
        mode={formMode}
        values={formValues}
        onChange={handleFormChange}
        onClose={closeModal}
        onSubmit={handleFormSubmit}
        saving={saving}
        error={error}
      />

      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        message={subjectToDelete ? t("pages.subjectBase.deleteConfirm", { name: subjectToDelete.name }) : ""}
      />
    </div>
  );
};

export default SubjectBasePage;
